import React from 'react';
import { Link } from 'react-router-dom';
import * as Cookies from '../utils/cookies';
import { getCities } from '../api/getCities';
import { useDevTools, useUpdateDevTools } from '../AppContext';

import { BUILD_DATE_FORMATTED } from '../buildDate';

import styles from './DevTools.module.css';
import { EnvironmentService } from '../services/Environment.service';

const isDevToolsEnabled = EnvironmentService.isStagingOrDev();

function DevTools(props) {
  const { refreshStates, quickSignInEnabled, quickSignUpEnabled } = props;
  const [tokenInput, setTokenInput] = React.useState(Cookies.getToken() || '');
  const [status, setStatus] = React.useState('');
  const [citiesStatus, setCitiesStatus] = React.useState('');
  const [citiesPreview, setCitiesPreview] = React.useState('');
  const devToolsSettings = useDevTools();
  const updateDevToolsSettings = useUpdateDevTools();

  const activeToken = Cookies.getToken() || '';

  const handleSwitchChange = key => event => {
    const isChecked = event.target.checked;

    updateDevToolsSettings(prevSettings => {
      const nextSettings = {
        ...prevSettings,
        [key]: isChecked,
      };


      if (key === 'devMode' && !isChecked) {
        nextSettings.preventRedirect = false;
        nextSettings.quickSignUp = false;
      }

      return nextSettings;
    });

    setStatus(
      `${key === 'devMode' ? 'Dev mode' : key === 'quickSignIn' ? 'SMS signin' : 'Quick sign-up'} ${
        isChecked ? 'enabled' : 'disabled'
      }.`
    );
  };

  const handleSetToken = () => {
    const nextToken = tokenInput.trim();

    if (!nextToken) {
      setStatus('Paste a token before setting it.');
      return;
    }

    Cookies.setToken(nextToken);
    setStatus('Token cookie updated.');
  };

  const handleClearAuth = () => {
    refreshStates();
    setTokenInput('');
    setStatus('User state and token were cleared.');
  };

  const handleClearStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
    setStatus('Local and session storage were cleared.');
  };

  const handleCopyToken = async () => {
    if (!activeToken || !navigator?.clipboard) {
      setStatus('No token found to copy.');
      return;
    }

    try {
      await navigator.clipboard.writeText(activeToken);
      setStatus('Token copied to clipboard.');
    } catch (error) {
      setStatus('Clipboard copy failed.');
    }
  };

  const handleCitiesPing = async () => {
    setCitiesStatus('Checking /api/configuration/cities/ ...');

    try {
      const data = await getCities();

      if (data?.error) {
        setCitiesStatus('Cities endpoint responded with an error.');
        setCitiesPreview(JSON.stringify(data.error, null, 2));
        return;
      }

      const cityCount = Array.isArray(data)
        ? data.length
        : Array.isArray(data?.results)
          ? data.results.length
          : 0;

      const previewData = Array.isArray(data)
        ? data.slice(0, 3)
        : Array.isArray(data?.results)
          ? data.results.slice(0, 3)
          : data;

      setCitiesStatus(`Cities endpoint is reachable (${cityCount} entries loaded).`);
      setCitiesPreview(JSON.stringify(previewData, null, 2));
    } catch (error) {
      setCitiesStatus('Cities endpoint request failed.');
      setCitiesPreview(JSON.stringify(error?.message || error, null, 2));
    }
  };

  if (!isDevToolsEnabled) {
    return (
      <main dir={'ltr'} className={styles.page}>
        <h1>Dev Tools</h1>
        <p>This page is available only in non-production environments.</p>
        <Link className={styles.linkButton} to="/signin">
          Back to sign in
        </Link>
      </main>
    );
  }

  return (
    <main dir={'ltr'} className={styles.page}>
      <h1>Dev Tools</h1>
      <p className={styles.subtitle}>
        Quick utilities for local debugging and integration checks.
      </p>

      <section className={styles.card}>
        <h2>Developer Switches</h2>
        <div className={styles.switchList}>
          <label className={styles.switchItem}>
            <input
              type="checkbox"
              checked={Boolean(devToolsSettings?.devMode)}
              onChange={handleSwitchChange('devMode')}
            />
            <span>Turn dev mode</span>
          </label>

           <label className={styles.switchItem}>
             <input
               type="checkbox"
               checked={Boolean(devToolsSettings?.preventRedirect)}
               onChange={handleSwitchChange('preventRedirect')}
               disabled={!devToolsSettings?.devMode}
             />
             <span>Prevent redirect</span>
           </label>

          <label className={styles.switchItem}>
            <input
              type="checkbox"
              checked={Boolean(devToolsSettings?.quickSignUp)}
              onChange={handleSwitchChange('quickSignUp')}
              disabled={!devToolsSettings?.devMode}
            />
            <span>Quick sign-up</span>
          </label>
        </div>

        <p className={styles.smallText}>
          Effective quick modes: sign-in {quickSignInEnabled ? 'on' : 'off'}, sign-up{' '}
          {quickSignUpEnabled ? 'on' : 'off'}.
        </p>
      </section>



       <section className={styles.card}>
         <h2>Environment</h2>
         <dl className={styles.metaList}>
           <div>
             <dt>NODE_ENV</dt>
             <dd>{process.env.NODE_ENV || '-'}</dd>
           </div>
           <div>
             <dt>Runtime environment</dt>
             <dd>{process.env.REACT_APP_RUNTIME_ENVIRONMENT || '-'}</dd>
           </div>
           <div>
             <dt>API URL</dt>
             <dd>{process.env.REACT_APP_API_URL || '-'}</dd>
           </div>
           <div>
             <dt>Build Date</dt>
             <dd>{BUILD_DATE_FORMATTED || '-'}</dd>
           </div>
         </dl>
       </section>

      <section className={styles.card}>
        <h2>Authentication</h2>
        <label htmlFor="dev-tools-token">Token</label>
        <textarea
          id="dev-tools-token"
          className={styles.textarea}
          rows={4}
          value={tokenInput}
          onChange={event => setTokenInput(event.target.value)}
          placeholder="Paste JWT token here"
        />

        <div className={styles.buttonRow}>
          <button type="button" onClick={handleSetToken}>
            Set token cookie
          </button>
          <button type="button" onClick={handleCopyToken}>
            Copy active token
          </button>
          <button type="button" onClick={handleClearAuth}>
            Clear auth state
          </button>
          <button type="button" onClick={handleClearStorage}>
            Clear browser storage
          </button>
        </div>

        <p className={styles.smallText}>
          Active token: {activeToken ? `${activeToken.slice(0, 16)}...` : 'none'}
        </p>
        {status && <p className={styles.status}>{status}</p>}
      </section>

      <section className={styles.card}>
        <h2>API Smoke Test</h2>
        <button type="button" onClick={handleCitiesPing}>
          Ping cities endpoint
        </button>
        {citiesStatus && <p className={styles.status}>{citiesStatus}</p>}
        {citiesPreview && <pre className={styles.preview}>{citiesPreview}</pre>}
      </section>

      <section className={styles.card}>
        <h2>Quick Navigation</h2>
        <div className={styles.linkRow}>
          <Link className={styles.linkButton} to="/signin">
            Open sign in
          </Link>
          <Link className={styles.linkButton} to="/leagues">
            Open leagues
          </Link>
          <Link className={styles.linkButton} to="/dev-tools/quick-signup">
            Quick signup (no auth)
          </Link>
        </div>
      </section>
    </main>
  );
}

export default DevTools;

